/**
 * Where a user's drill-down starts in the `GeoMapping` tree, by their `AccessLevel`.
 *
 * The tree's levels are Market (0) → Cluster (1) → Country (2), so a user who works at market
 * level starts one level below it, at clusters, and a country-level user has no geo level left and
 * goes straight to their partners. The level values that name a geography rather than a level
 * (`EMEA`, `NA`, `LATAM`, `India`, `GA`) and `WorldWide` all sit above the tree, so they start at
 * its roots.
 *
 * The depth is a starting point, not a promise about the payload's tree: `buildAttentionGeoNodes`
 * clamps it to the tree it actually gets, so an extra or a missing level in `GeoMapping` changes
 * how many steps the drill-down has, never whether it works.
 */
export const ATTENTION_GEO_START_DEPTH: Record<string, number> = {
  WorldWide: 0,
  EMEA: 0,
  NA: 0,
  LATAM: 0,
  India: 0,
  GA: 0,
  Market: 1,
  Cluster: 2,
  Country: 3,
}

/** Roots of the tree for a user whose access level the payload did not state. */
const DEFAULT_START_DEPTH = 0

export const getAttentionGeoStartDepth = (accessLevel?: string): number =>
  ATTENTION_GEO_START_DEPTH[accessLevel ?? ''] ?? DEFAULT_START_DEPTH

/** What a level of the tree is, by depth. Anything deeper keeps the neutral one. */
export type TAttentionGeoLevelId = 'market' | 'cluster' | 'country' | 'geo'

const GEO_LEVEL_IDS: TAttentionGeoLevelId[] = ['market', 'cluster', 'country']

const DEEPER_GEO_LEVEL_ID: TAttentionGeoLevelId = 'geo'

/**
 * The level's identity, not its wording: the UI turns it into a word through the tile's unit
 * keys, so a level is named in one place and translated in another.
 */
export const getAttentionGeoLevelId = (depth: number): TAttentionGeoLevelId =>
  GEO_LEVEL_IDS[depth] ?? DEEPER_GEO_LEVEL_ID
